import React from 'react';
import { Link } from 'react-router-dom';
import MemberName from '../components/MemberName';
import { insideMarkdownLink } from './ref-guards';
import SuppressedMention, {
  type MentionSuppressionOutcome,
} from '../components/chat/SuppressedMention';

/**
 * @mention rendering for team chats. A mention is "@" followed by an agent's
 * name (case-insensitive; names may contain spaces). At render time each
 * mention becomes a link to that agent's details page. Mirrors the backend
 * parseMentions matching (longest name first) — see
 * backend/src/chat-engine/mentions.ts.
 */

export interface MentionableAgent {
  id: number;
  name: string;
}

export interface SuppressedMentionAnnotation {
  agentId: number;
  start: number;
  end: number;
  outcome: MentionSuppressionOutcome;
  presentationText: string;
}

function validSuppressedMentions(
  text: string,
  annotations: SuppressedMentionAnnotation[] | undefined,
): SuppressedMentionAnnotation[] {
  const sorted = [...(annotations || [])].sort((a, b) => a.start - b.start);
  const valid: SuppressedMentionAnnotation[] = [];
  let previousEnd = 0;
  for (const annotation of sorted) {
    if (
      !Number.isInteger(annotation.start) ||
      !Number.isInteger(annotation.end) ||
      annotation.start < previousEnd ||
      annotation.start < 0 ||
      annotation.end <= annotation.start ||
      annotation.end > text.length ||
      text.slice(annotation.start, annotation.end) !==
        annotation.presentationText
    ) {
      continue;
    }
    valid.push(annotation);
    previousEnd = annotation.end;
  }
  return valid;
}

/**
 * Preserve the server's authoritative suppressed-mention offsets while
 * letting the caller render all ordinary text normally. Invalid or stale
 * annotations are ignored so they can never decorate unrelated text.
 */
export function renderWithSuppressedMentions(
  text: string,
  annotations: SuppressedMentionAnnotation[] | undefined,
  renderText: (text: string, key: string) => React.ReactNode,
): React.ReactNode {
  const valid = validSuppressedMentions(text, annotations);
  if (valid.length === 0) return renderText(text, 'text-all');

  const nodes: React.ReactNode[] = [];
  let last = 0;
  valid.forEach((annotation, index) => {
    if (annotation.start > last) {
      nodes.push(
        renderText(text.slice(last, annotation.start), `text-${index}`),
      );
    }
    nodes.push(
      <SuppressedMention
        key={`suppressed-${index}-${annotation.start}`}
        name={annotation.presentationText}
        outcome={annotation.outcome}
      />,
    );
    last = annotation.end;
  });
  if (last < text.length) {
    nodes.push(renderText(text.slice(last), 'text-tail'));
  }
  return nodes;
}

function reactAttributeName(name: string): string {
  switch (name) {
    case 'class':
      return 'className';
    case 'for':
      return 'htmlFor';
    case 'tabindex':
      return 'tabIndex';
    case 'colspan':
      return 'colSpan';
    case 'rowspan':
      return 'rowSpan';
    default:
      return name;
  }
}

const VOID_HTML_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

/**
 * Convert already-sanitized HTML into React nodes, swapping opaque text
 * tokens for real SuppressedMention components. Reconstructing the sanitized
 * tree keeps markup such as **bold**, lists, and links intact around a name.
 */
function sanitizedHtmlWithMentionTokens(
  html: string,
  replacements: Map<
    string,
    { annotation: SuppressedMentionAnnotation; key: string }
  >,
): React.ReactNode {
  const template = document.createElement('template');
  template.innerHTML = html;
  const tokens = [...replacements.keys()];
  const tokenPattern = new RegExp(
    `(${tokens.map(escapeRegExp).join('|')})`,
    'g',
  );

  const convert = (node: Node, key: string): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.textContent || '';
      if (!tokens.some((token) => value.includes(token))) return value;
      return value.split(tokenPattern).map((part, index) => {
        const replacement = replacements.get(part);
        return replacement ? (
          <SuppressedMention
            key={`${replacement.key}-${index}`}
            name={replacement.annotation.presentationText}
            outcome={replacement.annotation.outcome}
          />
        ) : (
          part
        );
      });
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const props: Record<string, unknown> = { key };
    for (const attribute of [...element.attributes]) {
      if (attribute.name === 'style') {
        const style: Record<string, string> = {};
        for (const property of [...element.style]) {
          const reactName = property.replace(/-([a-z])/g, (_, letter: string) =>
            letter.toUpperCase(),
          );
          style[reactName] = element.style.getPropertyValue(property);
        }
        props.style = style;
      } else {
        props[reactAttributeName(attribute.name)] = attribute.value;
      }
    }
    if (VOID_HTML_TAGS.has(tag)) return React.createElement(tag, props);
    const children = [...element.childNodes].map((child, index) =>
      convert(child, `${key}-${index}`),
    );
    return React.createElement(tag, props, ...children);
  };

  return [...template.content.childNodes].map((node, index) =>
    convert(node, `html-${index}`),
  );
}

/** Render Markdown without severing its syntax at suppressed-name offsets. */
export function renderMarkdownWithSuppressedMentions(
  text: string,
  annotations: SuppressedMentionAnnotation[] | undefined,
  renderMarkdown: (markdown: string) => string,
): React.ReactNode {
  const valid = validSuppressedMentions(text, annotations);
  if (valid.length === 0) {
    return (
      <div
        className="suppressed-mention-fragment"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(text) }}
      />
    );
  }

  let tokenPrefix = 'PDRSUPPRESSEDMENTION';
  while (text.includes(tokenPrefix)) tokenPrefix += 'X';
  const replacements = new Map<
    string,
    { annotation: SuppressedMentionAnnotation; key: string }
  >();
  let withTokens = text;
  [...valid].reverse().forEach((annotation, reverseIndex) => {
    const index = valid.length - reverseIndex - 1;
    const token = `${tokenPrefix}${index}TOKEN`;
    replacements.set(token, {
      annotation,
      key: `suppressed-${index}-${annotation.start}`,
    });
    withTokens =
      withTokens.slice(0, annotation.start) +
      token +
      withTokens.slice(annotation.end);
  });
  return sanitizedHtmlWithMentionTokens(
    renderMarkdown(withTokens),
    replacements,
  );
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function agentUrl(teamId: string | number, agentId: number): string {
  return `/console/team/${teamId}/agent/${agentId}`;
}

export interface MentionMatch {
  start: number;
  end: number;
  agentId: number;
  /** The literal matched text, e.g. "@Support Bot". */
  text: string;
}

/**
 * Non-overlapping @mention spans ordered by position. Longer agent names win
 * over shorter ones that are a prefix ("@Support Bot" beats "@Support").
 */
export function findMentions(
  text: string,
  agents: MentionableAgent[] | undefined,
): MentionMatch[] {
  const roster = (agents || []).filter(
    (agent) => agent.id != null && agent.name && agent.name.trim().length > 0,
  );
  if (!text || roster.length === 0) return [];
  const byLength = [...roster].sort((a, b) => b.name.length - a.name.length);
  const claimed: MentionMatch[] = [];
  for (const agent of byLength) {
    const pattern = new RegExp(
      `@${escapeRegExp(agent.name)}(?=$|[^\\p{L}\\p{N}_])`,
      'giu',
    );
    for (const match of text.matchAll(pattern)) {
      const start = match.index!;
      const end = start + match[0].length;
      // Skip a shorter name that overlaps an already-claimed longer mention.
      if (claimed.some((c) => start < c.end && end > c.start)) continue;
      claimed.push({ start, end, agentId: agent.id, text: match[0] });
    }
  }
  return claimed.sort((a, b) => a.start - b.start);
}

/** Whether text contains a complete @mention of one of the supplied agents. */
export function hasAgentMention(
  text: string,
  agents: MentionableAgent[] | undefined,
): boolean {
  return findMentions(text, agents).length > 0;
}


/**
 * Rewrite @mentions into markdown links before markdown rendering, so
 * sanitization applies normally (relative "/console/…" links survive
 * DOMPurify and navigate in-app). Mentions already inside link syntax are
 * left alone. Used for agent responses, which render as markdown.
 */
export function expandMentionsToMarkdown(
  text: string,
  agents: MentionableAgent[] | undefined,
  teamId: string | number,
): string {
  const matches = findMentions(text, agents);
  if (matches.length === 0) return text;
  let out = '';
  let last = 0;
  for (const match of matches) {
    if (insideMarkdownLink(text, match.start)) continue;
    // Team members are rostered with negative ids; there is no agent page
    // for them, so their mentions stay plain text in markdown output.
    if (match.agentId < 0) continue;
    out += text.slice(last, match.start);
    out += `[${match.text}](${agentUrl(teamId, match.agentId)})`;
    last = match.end;
  }
  out += text.slice(last);
  return out;
}

/**
 * Split plain text into nodes, turning @mentions into in-app links. Used for
 * user chat bubbles, which render as plain text (not markdown).
 */
export function renderTextWithMentions(
  text: string,
  agents: MentionableAgent[] | undefined,
  teamId: string | number | undefined,
): React.ReactNode {
  if (teamId == null) return text;
  const matches = findMentions(text, agents);
  if (matches.length === 0) return text;
  const nodes: React.ReactNode[] = [];
  let last = 0;
  matches.forEach((match, index) => {
    if (match.start > last) nodes.push(text.slice(last, match.start));
    // Negative ids are team members (see TeamChat's roster mapping):
    // their mentions open the member detail modal instead of an agent page
    nodes.push(
      match.agentId < 0 ? (
        <MemberName
          key={`mention-${index}`}
          teamId={String(teamId)}
          userId={-match.agentId}
          className="chat-mention">
          {match.text}
        </MemberName>
      ) : (
        <Link
          key={`mention-${index}`}
          to={agentUrl(teamId, match.agentId)}
          className="chat-mention">
          {match.text}
        </Link>
      ),
    );
    last = match.end;
  });
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}
